import { Box } from '@mui/material';
import { ResolverRow as ResolverRowType, StoredAlias, deriveRowAliasView, isRowFullyResolved } from '../reducer';
import { RESOLVER_GRID } from '../constants';
import ImportedTrackData from './ImportedTrackData';
import MappedAliases from './MappedAliases';
import AliasFeedback from './AliasFeedback';
import QwireTracksFields, { RowHandlers } from './QwireTracksFields';

type ResolverRowProps = {
  row: ResolverRowType;
  handlers: RowHandlers;
  divisionAliases: StoredAlias[];
  onStoredAliasDeleted: (alias: StoredAlias) => void;
  pendingNewLibraries?: string[];
};

const ResolverRow = ({
  row, handlers, divisionAliases, onStoredAliasDeleted, pendingNewLibraries,
}: ResolverRowProps) => {
  const aliasView = deriveRowAliasView(row, divisionAliases);
  /* Engine-matched rows surface through the "Show all tracks" toggle for REVIEW only:
     the BE rejects resolutions for items it matched itself, and no alias will be saved
     for them at FINISH — so the fields are disabled and the pending-alias chip and
     feedback are suppressed. Deleting a STORED alias stays available (it's the
     documented undo for an alias auto-match, effective from the next import). */
  const readOnly = row.engineMatched;

  return (
    <Box sx={{ display: 'grid', ...RESOLVER_GRID }}>
      <ImportedTrackData
        matched={isRowFullyResolved(row, divisionAliases)}
        lineNumbers={row.lineNumbers}
        vendorLibrary={row.vendorLibrary}
        vendorTitle={row.vendorTitle}
        use={row.use}
        duration={row.duration}
      />
      {/* Empty gutter track (RESOLVER_GRID) -- keeps QwireTracksFields from being pushed
          into the narrow column below. The copy control in TrackTitleSelect overhangs
          left over this space; it renders nothing itself. */}
      <Box aria-hidden="true" />
      <QwireTracksFields row={row} handlers={handlers} pendingNewLibraries={pendingNewLibraries} readOnly={readOnly} />
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <Box sx={{ position: 'relative', flex: 1 }}>
          <MappedAliases
            storedAliases={aliasView.storedAliases}
            pendingVendorTitle={readOnly ? null : aliasView.pendingVendorTitle}
            onDeleteStored={onStoredAliasDeleted}
            onDeletePending={() => handlers.onAliasRemoved(row.itemId)}
          />
        </Box>
        <Box sx={{ width: 110, flexShrink: 0 }}>
          {!readOnly && aliasView.feedback && (
            <AliasFeedback feedback={aliasView.feedback} conflictTrackTitle={aliasView.conflictTrackTitle} />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ResolverRow;
